import { Dialog, DialogSurface, DialogBody, DialogContent, DialogActions, Button, Checkbox, makeStyles } from "@fluentui/react-components";

import { useState } from "react";

// Import theme tokens
import { tokens } from "../../theme";
import { brand } from "@brand";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Spacing
    spacingVerticalXs: tokens.primitives["spacing-Vertical-XS"],
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
    spacingVerticalL: tokens.primitives["spacing-Vertical-L"],
    spacingVerticalXl: tokens.primitives["spacing-Vertical-XL"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],
    spacingHorizontalXl: tokens.primitives["spacing-Horizontal-XL"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontFamilyHeader: tokens.primitives["font-Family-Header"],
    fontSizeBase300: tokens.primitives["font-Size-Base-300"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontSizeBase500: tokens.primitives["font-Size-Base-500"],
    fontWeightRegular: tokens.primitives["font-Weight-Regular"],
    fontWeightMedium: tokens.primitives["font-Weight-Medium"],
    fontWeightSemiBold: tokens.primitives["font-Weight-SemiBold"],
    lineHeightBase400: tokens.primitives["lineHeight-Base-400"],
    lineHeightBase500: tokens.primitives["lineHeight-Base-500"],

    // Colors
    themePrimary: tokens.palette["theme-Primary"],
    themeDark: tokens.palette["theme-Dark"],
    themeDarkAlt: tokens.palette["theme-DarkAlt"],
    themeLighter: tokens.palette["theme-Lighter"],
    themeLighterAlt: tokens.palette["theme-LighterAlt"],
    neutralPrimary: tokens.semantic["neutral-Primary"],
    neutralWhite: tokens.semantic["neutral-White"],
    neutralLighterAlt: tokens.semantic["neutral-LighterAlt"],

    // Radius
    radiusLarge: tokens.primitives["stroke-Radius-Large"],
    radiusXLarge: tokens.primitives["stroke-Radius-XLarge"],

    // Shadows
    shadowFloatingSmall: tokens.shadows["shadow-Floating-Small"],

    // Buttons
    btnPrimaryBg: tokens.buttons.buttonSolidPrimaryBackground,
    btnPrimaryFg: tokens.buttons.buttonSolidPrimaryForeground,
    btnPrimaryBgHover: tokens.buttons.buttonSolidPrimaryBackgroundHover,
    btnPrimaryBgPressed: tokens.buttons.buttonSolidPrimaryBackgroundPressed,
    btnPrimaryBgDisabled: tokens.buttons.buttonSolidPrimaryBackgroundDisabled,
    btnPrimaryFgDisabled: tokens.buttons.buttonSolidPrimaryForegroundDisabled,

    // Stroke
    strokeFocus: tokens.primitives.strokeFocus
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    dialogSurface: {
        maxWidth: "420px",
        width: "90vw",
        padding: 0,
        borderRadius: t.radiusXLarge,
        boxShadow: t.shadowFloatingSmall,
        overflow: "hidden"
    },

    dialogHeader: {
        backgroundColor: t.themePrimary,
        padding: `${t.spacingVerticalM} ${t.spacingHorizontalXl}`,
        textAlign: "center"
    },

    dialogTitle: {
        color: t.neutralWhite,
        fontFamily: t.fontFamilyHeader,
        fontSize: t.fontSizeBase500,
        fontWeight: t.fontWeightSemiBold as unknown as number,
        margin: 0,
        lineHeight: t.lineHeightBase500
    },

    dialogBody: {
        padding: `${t.spacingVerticalL} ${t.spacingHorizontalXl}`
    },

    greeting: {
        color: t.neutralPrimary,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightBase400,
        textAlign: "center",
        margin: 0,
        marginBottom: t.spacingVerticalM
    },

    linksContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: t.spacingVerticalXs,
        padding: `${t.spacingVerticalS} 0`,
        marginBottom: t.spacingVerticalM
    },

    link: {
        color: t.themePrimary,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightMedium as unknown as number,
        textDecoration: "none",

        "&:hover": {
            color: t.themeDarkAlt,
            textDecoration: "underline"
        },

        "&:focus": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        },

        "&:active": {
            color: t.themeDark
        }
    },

    checkboxContainer: {
        display: "flex",
        justifyContent: "center",
        backgroundColor: t.neutralLighterAlt,
        padding: `${t.spacingVerticalS} ${t.spacingVerticalM}`,
        borderRadius: t.radiusLarge,
        marginBottom: t.spacingVerticalL
    },

    checkbox: {
        "& label": {
            color: t.neutralPrimary,
            fontFamily: t.fontFamilyBase,
            fontSize: t.fontSizeBase300,
            fontWeight: t.fontWeightRegular as unknown as number
        }
    },

    dialogActions: {
        padding: 0,
        justifyContent: "center"
    },

    continueButton: {
        minWidth: "120px",
        padding: `6px 24px`,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightMedium as unknown as number,
        borderRadius: t.radiusLarge,
        backgroundColor: t.btnPrimaryBg,
        color: t.btnPrimaryFg,
        border: "none",
        cursor: "pointer",
        transitionProperty: "background-color",
        transitionDuration: "0.2s",

        "&:hover:not(:disabled)": {
            backgroundColor: t.btnPrimaryBgHover
        },

        "&:active:not(:disabled)": {
            backgroundColor: t.btnPrimaryBgPressed
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        },

        "&:disabled": {
            backgroundColor: t.btnPrimaryBgDisabled,
            color: t.btnPrimaryFgDisabled,
            cursor: "not-allowed"
        }
    }
});

// =============================================================================
// COMPONENT
// =============================================================================

export default function TermsOfUsePopup(props: { handleUserContinue: () => void }) {
    const styles = useStyles();

    const [agreeChecked, setAgreeChecked] = useState(false);

    const handleChecked = () => {
        setAgreeChecked(!agreeChecked);
    };

    return (
        <Dialog open={true} modalType="alert">
            <DialogSurface className={styles.dialogSurface} aria-describedby="terms-greeting" aria-labelledby="terms-title">
                <div className={styles.dialogHeader}>
                    <h2 id="terms-title" className={styles.dialogTitle}>
                        Welcome
                    </h2>
                </div>

                <DialogBody className={styles.dialogBody}>
                    <DialogContent>
                        <p id="terms-greeting" className={styles.greeting}>
                            You must accept the Terms of Use and Privacy Policy prior to using {brand.name}.
                        </p>

                        <nav className={styles.linksContainer} aria-label="Legal documents">
                            <a href={"#/terms_of_use"} target="_blank" rel="noopener noreferrer" className={styles.link}>
                                Terms of Use
                            </a>
                            <a href={"#/privacy_policy"} target="_blank" rel="noopener noreferrer" className={styles.link}>
                                Privacy Policy
                            </a>
                        </nav>

                        <div className={styles.checkboxContainer}>
                            <Checkbox
                                checked={agreeChecked}
                                onChange={handleChecked}
                                label={`I agree to the above Terms of Use and Privacy Policy.`}
                                className={styles.checkbox}
                                id="terms-agreement-checkbox"
                            />
                        </div>
                    </DialogContent>

                    <DialogActions className={styles.dialogActions}>
                        <Button
                            appearance="primary"
                            disabled={!agreeChecked}
                            onClick={props.handleUserContinue}
                            className={styles.continueButton}
                            aria-label={!agreeChecked ? "Continue - please agree to the terms first" : "Continue"}
                        >
                            Continue
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}
